import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Zap, Send, Check, Loader2, Users, Mail, RefreshCw, Clock, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/superadmin/marketing';

export function ClientUpdatesTab({ storedKey }) {
  const [updateNotes, setUpdateNotes] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeView, setActiveView] = useState('compose'); // 'compose' | 'history'

  const headers = { 'X-Superadmin-Key': storedKey };

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/client-updates`, { headers });
      setHistory(data.updates || []);
    } catch {}
  };

  const generateEmail = async () => {
    if (!updateNotes.trim()) return toast.error('Descrivi l\'aggiornamento prima di generare.');
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/client-update/generate`, { update_notes: updateNotes }, { headers });
      setSubject(data.subject);
      setBodyHtml(data.body_html);
      toast.success('Email generata con AI! Controlla e invia.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione AI. Riprova.');
    } finally {
      setGenerating(false);
    }
  };

  const sendUpdate = async () => {
    if (!subject.trim() || !bodyHtml.trim()) return toast.error('Oggetto e corpo sono obbligatori.');
    setSending(true);
    setSendResult(null);
    try {
      const { data } = await axios.post(`${API}/client-update/send`, { subject, body_html: bodyHtml }, { headers });
      setSendResult(data);
      loadHistory();
      toast.success(`Aggiornamento inviato a ${data.sent} clienti.`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore durante l\'invio.';
      toast.error(msg);
      setSendResult({ error: msg });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="client-updates-tab">
      {/* Sub-nav */}
      <div className="flex items-center gap-1 mb-6 p-1 rounded-sm w-fit"
        style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-1)' }}>
        {[
          { id: 'compose', label: 'Nuovo aggiornamento' },
          { id: 'history', label: `Storico (${history.length})` },
        ].map(({ id, label }) => (
          <button key={id} onClick={() => setActiveView(id)}
            className="px-4 py-2 text-xs rounded-sm transition-all"
            style={{
              background: activeView === id ? 'var(--lux-gold)' : 'transparent',
              color: activeView === id ? '#000' : 'var(--lux-text-muted)',
            }}
            data-testid={`client-updates-nav-${id}`}>
            {label}
          </button>
        ))}
      </div>

      {activeView === 'compose' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: input + generate */}
          <div className="space-y-5">
            {/* How it works card */}
            <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
              <p className="text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--lux-gold)' }}>Come funziona</p>
              <div className="space-y-2.5 text-xs" style={{ color: 'var(--lux-text-muted)' }}>
                <p>1. Descrivi le novità in linguaggio semplice</p>
                <p>2. Claude scrive un'email professionale</p>
                <p>3. Controlla e modifica se necessario</p>
                <p>4. Invia a tutti i clienti attivi (no demo)</p>
              </div>
            </div>

            {/* Update notes */}
            <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
              <label className="block text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--lux-gold)' }}>
                Nota aggiornamento
              </label>
              <textarea
                value={updateNotes}
                onChange={e => setUpdateNotes(e.target.value)}
                rows={5}
                placeholder="Es: Abbiamo aggiunto il supporto per Loxone e BTicino nella smart home. Ora puoi controllare luci, tapparelle e termostati direttamente dal pannello admin. Aggiunto anche export CSV dei ricavi..."
                className="w-full px-3 py-2.5 text-sm rounded-sm resize-none"
                style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}
                data-testid="update-notes-input"
              />
              <button onClick={generateEmail} disabled={generating}
                className="mt-3 w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold rounded-sm"
                style={{ background: 'var(--lux-gold)', color: '#000' }}
                data-testid="generate-update-btn">
                {generating
                  ? <><Loader2 size={14} className="animate-spin" /> Generazione con AI...</>
                  : <><Zap size={14} /> Genera email con AI</>}
              </button>
            </div>
          </div>

          {/* Right: email editor + send */}
          <div className="lg:col-span-2">
            {generating && (
              <div className="p-8 rounded-sm text-center mb-4" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)' }}>
                <div className="w-7 h-7 border-2 rounded-full animate-spin mx-auto mb-3"
                  style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
                <p className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>
                  Claude sta scrivendo l'email di aggiornamento...
                </p>
              </div>
            )}

            <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
              <p className="text-xs uppercase tracking-widest mb-4" style={{ color: 'var(--lux-gold)' }}>Email da inviare</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--lux-text-muted-2)' }}>Oggetto</label>
                  <input value={subject} onChange={e => setSubject(e.target.value)}
                    placeholder="Genera con AI oppure scrivi qui l'oggetto..."
                    className="w-full px-4 py-3 text-sm rounded-sm"
                    style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}
                    data-testid="update-subject-input" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--lux-text-muted-2)' }}>Corpo (HTML)</label>
                    <span className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>
                      Variabile disponibile: {'{{nome}}'}
                    </span>
                  </div>
                  <textarea value={bodyHtml} onChange={e => setBodyHtml(e.target.value)}
                    rows={12}
                    placeholder="Genera con AI oppure scrivi qui il corpo in HTML..."
                    className="w-full px-4 py-3 text-sm rounded-sm font-mono resize-y"
                    style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)', minHeight: '220px' }}
                    data-testid="update-body-input" />
                </div>

                {/* Send */}
                <div className="flex items-center gap-4 pt-1">
                  <button onClick={sendUpdate} disabled={sending || !subject.trim() || !bodyHtml.trim()}
                    className="flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: 'var(--lux-gold)', color: '#000' }}
                    data-testid="send-update-btn">
                    {sending ? <><Loader2 size={14} className="animate-spin" /> Invio in corso...</> : <><Send size={14} /> Invia a tutti i clienti</>}
                  </button>
                  <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
                    Solo clienti attivi (no demo)
                  </p>
                </div>

                {/* Result */}
                {sendResult && !sendResult.error && (
                  <div className="p-4 rounded-sm" style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <Check size={15} style={{ color: '#059669' }} />
                      <p className="text-sm font-medium" style={{ color: '#059669' }}>Inviato con successo</p>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>
                      {sendResult.sent} email consegnate · {sendResult.failed} errori · {sendResult.total} clienti totali
                    </p>
                  </div>
                )}
                {sendResult?.error && (
                  <div className="p-4 rounded-sm" style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)' }}>
                    <p className="text-sm" style={{ color: '#dc2626' }}>{sendResult.error}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      {activeView === 'history' && (
        <div>
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-serif text-xl font-light" style={{ color: 'var(--lux-text)' }}>
              Aggiornamenti inviati
            </h3>
            <button onClick={loadHistory} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm"
              style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
              <RefreshCw size={12} /> Aggiorna
            </button>
          </div>
          {history.length === 0 ? (
            <div className="text-center py-16" style={{ color: 'var(--lux-text-muted)' }}>
              <Mail size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Nessun aggiornamento inviato ancora.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((u, i) => (
                <div key={i} className="flex items-center justify-between p-5 rounded-sm"
                  style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
                  <div>
                    <p className="font-medium text-sm" style={{ color: 'var(--lux-text)' }}>{u.subject}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>
                      {new Date(u.sent_at).toLocaleDateString('it-IT', {
                        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                      })} · {u.recipients_total} clienti
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium" style={{ color: '#059669' }}>{u.sent} inviate</p>
                    {u.failed > 0 && <p className="text-xs" style={{ color: '#dc2626' }}>{u.failed} errori</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
